import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  CircularProgress,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import FastForwardIcon from '@mui/icons-material/FastForward';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../types/vehicle';
import { reservationService } from '../../services/reservationService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface VehicleAvailability {
  vehicle: Vehicle;
  available: boolean;
  loading: boolean;
}

interface VehicleSelectorProps {
  vehicles: Vehicle[];
  loading: boolean;
  onSelectVehicle: (vehicle: Vehicle) => void;
  selectedVehicleId?: string;
  selectedStartDate?: Date | null;
  selectedEndDate?: Date | null;
  onModifyDates?: () => void;
  onViewCalendar?: (vehicleId: string) => void;
  onApplyNextSlot?: (vehicleId: string) => void;
}

const VehicleSelector: React.FC<VehicleSelectorProps> = ({
  vehicles,
  loading,
  onSelectVehicle,
  selectedVehicleId,
  selectedStartDate,
  selectedEndDate,
  onModifyDates,
  onViewCalendar,
  onApplyNextSlot
}) => {
  const { t } = useTranslation();
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [vehiclesAvailability, setVehiclesAvailability] = useState<VehicleAvailability[]>([]);

  useEffect(() => {
    checkAvailability();
  }, [vehicles, selectedStartDate, selectedEndDate]);

  const checkAvailability = async () => {
    if (!selectedStartDate || !selectedEndDate) {
      setVehiclesAvailability(vehicles.map(v => ({ vehicle: v, available: true, loading: false })));
      return;
    }

    // Initialiser avec loading=true
    setVehiclesAvailability(vehicles.map(v => ({ vehicle: v, available: false, loading: true })));

    // Vérifier la disponibilité de chaque véhicule
    const availabilityPromises = vehicles.map(async (vehicle) => {
      try {
        const available = await reservationService.checkVehicleAvailability(
          vehicle.id,
          selectedStartDate,
          selectedEndDate
        );
        return { vehicle, available, loading: false };
      } catch (error) {
        console.error(`Error checking availability for vehicle ${vehicle.id}:`, error);
        return { vehicle, available: false, loading: false };
      }
    });

    const results = await Promise.all(availabilityPromises);
    setVehiclesAvailability(results);
  };

  const formatPeriod = () => {
    if (!selectedStartDate || !selectedEndDate) {
      return t('reservation:publicPage.selectDatesFirst', 'Sélectionnez d\'abord les dates');
    }

    const start = format(selectedStartDate, 'EEE. dd/MM/yyyy HH:mm', { locale: fr });
    const end = format(selectedEndDate, 'EEE. dd/MM/yyyy HH:mm', { locale: fr });
    return `${start} → ${end}`;
  };

  const filteredVehicles = showOnlyAvailable
    ? vehiclesAvailability.filter(v => v.available)
    : vehiclesAvailability;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Affichage de la période sélectionnée */}
      {(selectedStartDate && selectedEndDate) && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {t('reservation:publicPage.period', 'Période')} : {formatPeriod()}
            </Typography>
            {onModifyDates && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={onModifyDates}
                sx={{ textTransform: 'none' }}
              >
                {t('reservation:publicPage.modifyDates', 'Modifier dates')}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {/* Toggle "Seulement disponibles" */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyAvailable}
              onChange={(e) => setShowOnlyAvailable(e.target.checked)}
              color="primary"
            />
          }
          label={t('reservation:publicPage.onlyAvailable', 'Seulement disponibles')}
        />
      </Box>

      {/* Liste des véhicules */}
      {filteredVehicles.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {showOnlyAvailable
              ? t('reservation:publicPage.noAvailableVehicles', 'Aucun véhicule disponible pour cette période')
              : t('reservation:publicPage.noVehicles', 'Aucun véhicule dans cette flotte')}
          </Typography>
        </Paper>
      ) : (
        <List sx={{ bgcolor: 'background.paper' }}>
          {filteredVehicles.map((item, index) => (
            <React.Fragment key={item.vehicle.id}>
              <ListItem
                sx={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  py: 2,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {/* Nom du véhicule et badge de statut */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, width: '100%' }}>
                  <Typography variant="h6">
                    {item.vehicle.make} {item.vehicle.model}
                  </Typography>

                  {item.loading ? (
                    <CircularProgress size={24} />
                  ) : item.available ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={t('reservation:publicPage.available', 'Disponible')}
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<CancelIcon />}
                      label={t('reservation:publicPage.unavailable', 'Indisponible')}
                      color="error"
                      size="small"
                    />
                  )}
                </Box>

                {/* Informations supplémentaires */}
                <ListItemText
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.secondary">
                        {item.vehicle.year} • {item.vehicle.licensePlate}
                      </Typography>
                      {item.vehicle.fuelType && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' • '}{t(`vehicle.fuelType.${item.vehicle.fuelType}`, item.vehicle.fuelType)}
                        </Typography>
                      )}
                    </>
                  }
                />

                {/* Boutons d'action */}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  {item.available ? (
                    <Button
                      variant="contained"
                      onClick={() => onSelectVehicle(item.vehicle)}
                      disabled={item.loading}
                    >
                      {t('reservation:publicPage.select', 'Sélectionner')}
                    </Button>
                  ) : (
                    <>
                      {onApplyNextSlot && selectedStartDate && selectedEndDate && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FastForwardIcon />}
                          onClick={() => onApplyNextSlot(item.vehicle.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('reservation:publicPage.applyNextSlot', 'Appliquer le prochain créneau')}
                        </Button>
                      )}
                      {onViewCalendar && (
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<EventIcon />}
                          onClick={() => onViewCalendar(item.vehicle.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('reservation:publicPage.viewWeek', 'Voir la semaine')}
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </ListItem>
              {index < filteredVehicles.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};

export default VehicleSelector;
